import { type ComponentProps } from "react";
import {
  BookOpen,
  LayoutDashboard,
  FileChartColumnIncreasing,
  ToolCase,
  LayoutTemplateIcon,
  FolderKanban,
} from "lucide-react";
import { NavMain } from "../nav-main";
import { NavUser } from "../nav-user";
import { useSession } from "@/platform/auth";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/shared/components/ui/sidebar";
import { BrandLogo } from "./components/brand-logo";
import { SidebarCreditMeter } from "./components/sidebar-credit-meter";
import { useTenant } from "@/domains/tenant/hooks";
import { type FeatureFlag } from "@/types/supabase.types";
import { useQuery } from "@tanstack/react-query";
import { profileQueries } from "@/features/profile";
import { billingQueries } from "@/domains/billing/queries";

type NavItem = {
  title: string;
  url: string;
  icon?: typeof FileChartColumnIncreasing;
  requiredFeatures?: FeatureFlag[];
};

const NAV_ITEMS: NavItem[] = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    title: "Reports",
    url: "/reports",
    icon: FileChartColumnIncreasing,
  },
  {
    title: "Template Builder",
    url: "/template-builder",
    icon: LayoutTemplateIcon,
    requiredFeatures: ["report-builder"],
  },
  {
    title: "Resources",
    url: "/resources",
    icon: FolderKanban,
  },
  {
    title: "Mini Tools",
    url: "/mini-tools",
    icon: ToolCase,
    requiredFeatures: ["mini-tools"],
  },
] as const;

const DOCS_URL =
  "https://researchly.notion.site/Researchly-App-Dokumentation-29de185400fb80d0a69beb1a49bb304c";

export function AppSidebar({ ...props }: ComponentProps<typeof Sidebar>) {
  const { user } = useSession();
  const { data: tenantData } = useTenant();
  const { data: profileData } = useQuery(profileQueries.current(user?.id));
  const { data: creditUsage } = useQuery(billingQueries.currentCreditUsage());

  const tenantFeatures = tenantData?.setting?.setting_features_enum;
  const tenantName = tenantData?.tenant?.tenant_name;

  const hasProfileFeature = (tenantFeatures ?? []).includes("profile");

  const navItems = NAV_ITEMS.filter((item) => {
    const required = item.requiredFeatures ?? [];
    if (!required || required.length === 0) return true;
    const features = tenantFeatures ?? [];
    if (features.length === 0) return false;
    const set = new Set<FeatureFlag>(features);
    return required.every((feature) => set.has(feature));
  });

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <BrandLogo />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navItems} />
      </SidebarContent>
      <SidebarFooter className="gap-1.5">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Docs"
              className="h-9 rounded-lg px-2.5 font-medium text-sidebar-foreground/75 hover:text-sidebar-foreground"
            >
              <a href={DOCS_URL} target="_blank" rel="noreferrer">
                <BookOpen />
                <span>Docs</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarCreditMeter
          usage={creditUsage}
          linkToDetails={hasProfileFeature}
        />
        {user ? (
          <NavUser
            user={user}
            tenantFeatures={tenantFeatures}
            tenantName={tenantName}
            profileData={profileData}
          />
        ) : null}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
